<template>
  <div class="user-page">
    <!-- 左侧部门树 -->
    <div class="dept-panel">
      <div class="panel-title">部门结构</div>
      <el-input v-model="deptFilter" placeholder="搜索部门" clearable size="small" style="margin-bottom: 12px" />
      <el-tree
        ref="deptTreeRef"
        :data="deptTree"
        node-key="id"
        default-expand-all
        :props="{ label: 'deptName', children: 'children' }"
        :filter-node-method="filterDept"
        highlight-current
        @node-click="onDeptClick"
      >
        <template #default="{ node, data }">
          <span class="dept-node">
            <el-icon><OfficeBuilding /></el-icon>
            <span>{{ data.deptName }}</span>
            <el-badge v-if="getUserCount(data)" :value="getUserCount(data)" class="dept-badge" />
          </span>
        </template>
      </el-tree>
    </div>

    <!-- 右侧用户列表 -->
    <div class="user-panel">
      <div class="toolbar">
        <div class="actions">
          <el-button type="primary" @click="openModal">新建用户</el-button>
        </div>
        <div class="filters">
          <el-input v-model="query.realName" placeholder="姓名" clearable style="width: 160px" @keyup.enter="loadUsers" />
          <el-select v-model="query.status" placeholder="状态" clearable style="width: 120px" @change="loadUsers">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
          <el-button type="primary" @click="loadUsers">查询</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </div>
      </div>

      <el-table stripe :data="users" border height="calc(100vh - 280px)" style="width:100%">
        <el-table-column prop="username" label="用户名" min-width="120" />
        <el-table-column prop="realName" label="真实姓名" width="100" />
        <el-table-column prop="deptName" label="所属部门" width="140" />
        <el-table-column prop="roleName" label="角色" width="120">
          <template #default="{ row }">
            <el-tag v-if="row.roleName" size="small" type="warning">{{ row.roleName }}</el-tag>
            <span v-else style="color:#c0c4cc">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="170">
          <template #default="{ row }">{{ formatDateTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="editUser(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDeleteUser(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="formData.id ? '编辑用户' : '新建用户'" width="520px">
      <el-form :model="formData" label-width="90px">
        <el-form-item label="用户名" required>
          <el-input v-model="formData.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" :required="!formData.id">
          <el-input v-model="formData.password" type="password" :placeholder="formData.id ? '不修改请留空' : '请输入密码'" />
        </el-form-item>
        <el-form-item label="真实姓名" required>
          <el-input v-model="formData.realName" placeholder="请输入真实姓名" />
        </el-form-item>
        <el-form-item label="所属部门">
          <el-tree-select v-model="formData.deptId" :data="deptTree"
                          :props="{ label: 'deptName', value: 'id', children: 'children' }"
                          placeholder="请选择部门" clearable check-strictly />
        </el-form-item>
        <el-form-item label="系统角色">
          <el-select v-model="formData.roleId" placeholder="请选择角色" clearable style="width:100%">
            <el-option v-for="r in roles" :key="r.id" :label="r.roleName" :value="r.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="formData.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="formData.status">
            <el-radio :value="1">启用</el-radio>
            <el-radio :value="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveUser">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { OfficeBuilding } from '@element-plus/icons-vue'
import { getUserList, saveUser, deleteUser, getRoleList, getDeptTree } from '../api'
import { formatDateTime } from '../utils/format'

const users = ref([])
const roles = ref([])
const deptTree = ref([])
const dialogVisible = ref(false)
const deptFilter = ref('')
const deptTreeRef = ref(null)
const currentDeptId = ref(null)

const query = reactive({ realName: '', status: null, deptId: null })
const formData = reactive({
  id: null, username: '', password: '', realName: '', phone: '',
  deptId: null, roleId: null, status: 1
})

onMounted(async () => {
  await Promise.all([loadUsers(), loadRoles(), loadDeptTree()])
})

watch(deptFilter, (v) => deptTreeRef.value?.filter(v))

function filterDept(value, data) {
  if (!value) return true
  return data.deptName?.includes(value)
}

async function loadDeptTree() {
  const res = await getDeptTree()
  deptTree.value = res.data || []
}

async function loadRoles() {
  const res = await getRoleList()
  roles.value = res.data || []
}

async function loadUsers() {
  const res = await getUserList(query)
  users.value = res.data || []
}

function getUserCount(dept) {
  // 递归统计该部门及所有子部门的用户数
  let count = 0
  users.value.forEach(u => { if (u.deptId === dept.id) count++ })
  dept.children?.forEach(c => count += getUserCount(c))
  return count
}

function onDeptClick(node) {
  currentDeptId.value = node.id
  query.deptId = node.id
  loadUsers()
}

const resetQuery = () => {
  Object.assign(query, { realName: '', status: null, deptId: null })
  currentDeptId.value = null
  deptTreeRef.value?.setCurrentKey(null)
  loadUsers()
}

const openModal = () => {
  Object.assign(formData, {
    id: null, username: '', password: '', realName: '', phone: '',
    deptId: currentDeptId.value || null, roleId: null, status: 1
  })
  dialogVisible.value = true
}

const editUser = (row) => {
  Object.assign(formData, { ...row, password: '' })
  dialogVisible.value = true
}

const handleSaveUser = async () => {
  if (!formData.username) { ElMessage.warning('请输入用户名'); return }
  if (!formData.id && !formData.password) { ElMessage.warning('请输入密码'); return }
  await saveUser(formData)
  ElMessage.success('保存成功')
  dialogVisible.value = false
  loadUsers()
}

const handleDeleteUser = async (id) => {
  await deleteUser(id)
  ElMessage.success('删除成功')
  loadUsers()
}
</script>

<style scoped>
.user-page {
  display: flex;
  height: calc(100vh - 124px);
  gap: 16px;
}
.dept-panel {
  width: 240px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}
.dept-node {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  font-size: 14px;
}
.dept-badge {
  margin-left: auto;
}
.user-panel {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.actions, .filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
